// src/utils/CbamProgress.js

export const isFilled = (value) => {
  if (value === null || value === undefined) return false;

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  if (typeof value === "number") {
    return !Number.isNaN(value);
  }

  if (typeof value === "boolean") {
    return true;
  }

  // For Dayjs values
  if (value?.isValid && typeof value.isValid === "function") {
    return value.isValid();
  }

  // For JavaScript Date values
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  return false;
};

export const getValueByPath = (obj, path) => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

export const calculateRequiredProgress = (
  data,
  requiredFields = []
) => {
  const total = requiredFields.length;

  const filled = requiredFields.filter((fieldPath) =>
    isFilled(getValueByPath(data, fieldPath))
  ).length;

  const progress =
    total === 0 ? 0 : Math.round((filled / total) * 100);

  return {
    total,
    filled,
    progress,
    status:
      progress === 100
        ? "Completed"
        : progress > 0
        ? "In Progress"
        : "Not Started",
  };
};

export const calculateRequiredProgressForRows = (
  rows = [],
  requiredFields = []
) => {
  const total = rows.length * requiredFields.length;

  const filled = rows.reduce((count, row) => {
    return (
      count +
      requiredFields.filter((fieldPath) =>
        isFilled(getValueByPath(row, fieldPath))
      ).length
    );
  }, 0);

  const progress =
    total === 0 ? 0 : Math.round((filled / total) * 100);

  return {
    total,
    filled,
    progress,
    status:
      progress === 100
        ? "Completed"
        : progress > 0
        ? "In Progress"
        : "Not Started",
  };
};



/* =========================================================
   FORM A
========================================================= */

export const FORM_A_REQUIRED_FIELDS = {
  reportingPeriod: [
    "auditName",
    "auditDescription",
    "reportingFrequency",
    "reportingQuarter",
    "reportingYear",
    "reportingPeriodStart",
    "reportingPeriodEnd",
  ],

  installation: [
    "installationName",
    "street",
    "economicActivity",
    "postCode",
    "city",
    "country",
    "latitude",
    "longitude",
    "nameAuthorizedRepresentative",
    "email",
    "telephone",
  ],

  verifier: [
    "verifierCompanyName",
    "verifierStreet",
    "verifierCity",
    "verifierPostcode",
    "verifierCountry",
    "verifierRepresentativeName",
    "verifierEmail",
    "verifierTelephone",
    "verifierAccreditationMemberState",
    "verifierAccreditationNationalAuthority",
    "verifierRegistrationNumber",
  ],

  aggregatedGoods: [
    "cnCode",
  "category",
  "primaryRoute",
  "route1",
  "route2",
  "route3",
  "route4",
  "route5",
  "route6",
],

  relevantProcesses: [
  "aggregatedGoodsCategory",
  "name",
  "inc1",
  "inc2",
  "inc3",
  "inc4",
  "inc5",
  "inc6",
],

  purchasedPrecursors: [
  "goodsCategory",
  "countryCode",
  "route1",
  "route2",
  "route3",
  "route4",
  "route5",
  "name",
  "error",
],
};

/* =========================================================
   FORM B
========================================================= */

export const FORM_B_REQUIRED_FIELDS = {
  sourceStreams: [
    "method",
    "sourceName",
    "fuelType",
    "activityData",
    "ncv",
    "ef",
    "efSource",
    "carbonContent",
    "oxidationFactor",
    "convFactor",
    "biomassContent",
  ],

  pfcEmissions: [
    "method",
    "technologyType",
    "activityData",
    "frequency",
    "duration",
    "sefCF4",
    "aeo",
    "ce",
    "ovc",
    "fC2F6",
    "gwpCF4",
    "gwpC2F6",
    "collectionEfficiency",
  ],

  measurementBased: [
    "name",
    "ghgType",
    "activityData",
    "biomassContent",
    "hourlyGHGConc",
    "hoursOperating",
    "flueGasFlow",
    "annualAmountOfGHG",
    "gwp",
  ],
};

/* =========================================================
   FORM C
========================================================= */

export const FORM_C_REQUIRED_FIELDS = {
  fuelBalance: [
    "fuelBalance.manual.totalFuel",
    "fuelBalance.manual.cbam",
    "fuelBalance.manual.electricity",
    "fuelBalance.manual.nonCbam",
  ],

  ghgBalance: [
    "ghgBalance.manual.co2",
    "ghgBalance.manual.biomass",
    "ghgBalance.manual.n2o",
    "ghgBalance.manual.pfc",
    "ghgBalance.manual.indirect",
    "ghgBalance.fromSheet.n2o",
    "ghgBalanceByMethod.other"

  ],
};

/* =========================================================
   FORM D
========================================================= */

export const FORM_D_REQUIRED_FIELDS = {
  alwaysRequired: [
    "productionLevels.0.unit",
    "productionLevels.0.amounts",
    "productionLevels.1.unit",
    "productionLevels.1.amounts",
    "productionLevels.2.unit",
    "productionLevels.2.amounts",
    "productionLevels.3.unit",
    "productionLevels.3.amounts",
    "productionLevels.4.unit",
    "productionLevels.4.amounts",
    "productionLevels.5.unit",
    "productionLevels.5.amounts",
    "productionLevels.6.unit",
    "productionLevels.6.amounts",
    "productionLevels.7.unit",
    "productionLevels.7.amounts",

    "productionDetails.producedForMarket",
    "productionDetails.amountsProducedForMarket",
    "productionDetails.shareOfTotalProducedForMarket",
    "productionDetails.totalProductionOnlyForMarket",

    "consumedOtherProcesses.0.unit",
    "consumedOtherProcesses.0.amounts",
    "consumedOtherProcesses.1.unit",
    "consumedOtherProcesses.1.amounts",
    "consumedOtherProcesses.2.unit",
    "consumedOtherProcesses.2.amounts",
    "consumedOtherProcesses.3.unit",
    "consumedOtherProcesses.3.amounts",
    "consumedOtherProcesses.4.unit",
    "consumedOtherProcesses.4.amounts",
    "consumedOtherProcesses.5.unit",
    "consumedOtherProcesses.5.amounts",
    "consumedOtherProcesses.6.unit",
    "consumedOtherProcesses.6.amounts",
    "consumedOtherProcesses.7.unit",
    "consumedOtherProcesses.7.amounts",
    "consumedOtherProcesses.8.unit",
    "consumedOtherProcesses.8.amounts",

    

    "consumedNonCbam.unit",
    "consumedNonCbam.amounts",

    "directlyAttributableEmissions",

    "electricityExported.amountsExported",
    "electricityExported.emissionFactor",
  ],

  measurableHeat: [
    "measurableHeat.amountImported",
    "measurableHeat.amountExported",
    "measurableHeat.emissionFactorImported",
    "measurableHeat.emissionFactorExported",
  ],

  wasteGases: [
    "wasteGases.amountImported",
    "wasteGases.amountExported",
    "wasteGases.emissionFactorImported",
    "wasteGases.emissionFactorExported",
  ],

  indirectEmissions: [
    "electricityConsumption.consumption",
    "electricityConsumption.emissionFactor",
    "electricityConsumption.source",
  ],
};

/* =========================================================
   FORM E
========================================================= */

export const FORM_E_REQUIRED_FIELDS = {
  alwaysRequired: [
    // (a) Total purchased levels - Unit + Amounts
    "totalPurchasedLevels.0.unit",
    "totalPurchasedLevels.0.amounts",
    "totalPurchasedLevels.1.unit",
    "totalPurchasedLevels.1.amounts",
    "totalPurchasedLevels.2.unit",
    "totalPurchasedLevels.2.amounts",
    "totalPurchasedLevels.3.unit",
    "totalPurchasedLevels.3.amounts",
    "totalPurchasedLevels.4.unit",
    "totalPurchasedLevels.4.amounts",
    // "totalPurchasedLevels.5.unit",
    // "totalPurchasedLevels.5.amounts",
    // "totalPurchasedLevels.6.unit",
    // "totalPurchasedLevels.6.amounts",
    // "totalPurchasedLevels.7.unit",
    // "totalPurchasedLevels.7.amounts",

    // (b) Consumed in production processes - Unit + Amounts only
    "consumedInProcesses.0.unit",
    "consumedInProcesses.0.amounts",
    "consumedInProcesses.0.process",
    "consumedInProcesses.1.unit",
    "consumedInProcesses.1.amounts",
    "consumedInProcesses.1.process",
    "consumedInProcesses.2.unit",
    "consumedInProcesses.2.amounts",
    "consumedInProcesses.2.process",
    "consumedInProcesses.3.unit",
    "consumedInProcesses.3.amounts",
    "consumedInProcesses.3.process",
    "consumedInProcesses.4.unit",
    "consumedInProcesses.4.amounts",
    "consumedInProcesses.4.process",
    "consumedInProcesses.5.unit",
    "consumedInProcesses.5.amounts",
    "consumedInProcesses.5.process",
    "consumedInProcesses.6.unit",
    "consumedInProcesses.6.amounts",
    "consumedInProcesses.6.process",
    "consumedInProcesses.7.unit",
    "consumedInProcesses.7.amounts",
    "consumedInProcesses.7.process",
    "consumedInProcesses.8.unit",
    "consumedInProcesses.8.amounts",
    "consumedInProcesses.8.process",
    "consumedInProcesses.9.unit",
    "consumedInProcesses.9.amounts",
    "consumedInProcesses.9.process",

    // (c) Other purposes
    "consumedOtherPurposes.0.unit",
    "consumedOtherPurposes.0.amounts",
    // "consumedOtherPurposes.1.unit",
    // "consumedOtherPurposes.1.amounts",

    // (d) Control
    // "control.0.unit",
    // "control.0.amounts",
    // "control.1.unit",
    // "control.1.amounts",

    // Specific embedded emissions - yellow value fields only
    "emissions.seeDirect",
    "emissions.sourceSeeDirect",
    "emissions.secIndirect",
    "emissions.secIndirectSource",
    "emissions.eefIndirect",
  ],

  case2Conditional: [
    "emissions.cnCode",
    "emissions.eefIndirectSource",
    "emissions.justification",
  ],
};

/* =========================================================
   FORM F
========================================================= */

export const FORM_F_REQUIRED_FIELDS = {
  cogeneration: [
    "title",
    "fuelInputIntoCHP",
    "heatOutputFromCHP",
    "electricityOutputFromCHP_mwh",
    "ghg_from_fuel_input",
    "ghg_from_flue_gas_cleaning",
    "default_eff_heat",
    "default_eff_elec",
    "ref_eff_heat_production",
    "ref_eff_electricity_production",
  ],

  carbonPrice: [
    "share_of_emissions_covered",
    "carbon_price_due_local",
    "amount_of_rebate_local",
  ],
};